import { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { EmptyState } from '../../../components/EmptyState';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/Tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Skeleton } from '@/components/Skeleton';
import toast from 'react-hot-toast';
import { useLeaveStore, LeaveRequest } from '../../../store/leaveStore';
import { useRbacStore } from '../../../store/rbacStore';
import { CalendarDays, PlusCircle, Clock, CheckCircle2, XCircle, FileText, Users, UserCheck } from 'lucide-react';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected' | 'cancelled';

export function LeaveManagement() {
  const {
    leaveRequests, balances, leaveTypes, teamCalendar, holidays, myApprover,
    fetchLeaveRequests, fetchBalances, fetchLeaveTypes, fetchTeamCalendar,
    fetchHolidays, fetchMyApprover, submitLeaveRequest, cancelLeaveRequest, isLoading,
  } = useLeaveStore();

  const { hasFullAccess, hasPermission } = useRbacStore();
  const canApprove = hasFullAccess() || hasPermission('Leave', 'Leave Requests', 'Approve');

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const [formData, setFormData] = useState({
    leaveTypeId: '',
    startDate: '',
    endDate: '',
    halfDay: false,
    halfDayType: 'first' as 'first' | 'second',
    reason: '',
  });
  const [selectedManagerId, setSelectedManagerId] = useState('');

  useEffect(() => {
    fetchLeaveRequests();
    fetchBalances();
    fetchLeaveTypes();
    fetchTeamCalendar();
    fetchHolidays();
    fetchMyApprover();
  }, []);

  // Set default leave type once types load
  useEffect(() => {
    if (leaveTypes.length > 0 && !formData.leaveTypeId) {
      setFormData(prev => ({ ...prev, leaveTypeId: leaveTypes[0].id }));
    }
  }, [leaveTypes]);

  // Default the approver to the primary manager once they load.
  useEffect(() => {
    const first = myApprover?.managers?.[0]?.id;
    if (first && !selectedManagerId) setSelectedManagerId(first);
  }, [myApprover]); // eslint-disable-line react-hooks/exhaustive-deps

  // --------------------------------------------------------------- computed

  const filteredRequests = useMemo(() => {
    if (statusFilter === 'all') return leaveRequests;
    return leaveRequests.filter(r => r.status === statusFilter);
  }, [leaveRequests, statusFilter]);

  const pendingApprovals = useMemo(
    () => leaveRequests.filter(r => r.status === 'pending'),
    [leaveRequests],
  );

  const selectedTypeBalance = useMemo(() => {
    if (!formData.leaveTypeId) return null;
    const lt = leaveTypes.find(t => t.id === formData.leaveTypeId);
    if (!lt) return null;
    const bal = balances.find(b => b.code === lt.code);
    return bal ?? null;
  }, [formData.leaveTypeId, leaveTypes, balances]);

  const calculatedDays = useMemo(() => {
    if (!formData.startDate || !formData.endDate) return 0;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (end < start) return 0;
    const diff = Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return formData.halfDay ? 0.5 : diff;
  }, [formData.startDate, formData.endDate, formData.halfDay]);

  // --------------------------------------------------------------- handlers

  const handleApply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.startDate || !formData.endDate || !formData.reason) {
      toast.error('Please fill all required fields');
      return;
    }
    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      toast.error('Start date must be on or before end date');
      return;
    }
    if (calculatedDays <= 0) {
      toast.error('Total days must be greater than 0');
      return;
    }

    try {
      await submitLeaveRequest({
        leaveTypeId: formData.leaveTypeId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        totalDays: calculatedDays,
        reason: formData.reason,
        reportingManagerId: selectedManagerId || undefined,
      });
      toast.success('Leave request submitted successfully!');
      setFormData({ leaveTypeId: leaveTypes[0]?.id || '', startDate: '', endDate: '', halfDay: false, halfDayType: 'first', reason: '' });
      fetchLeaveRequests();
      fetchBalances();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to submit');
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await cancelLeaveRequest(id);
      toast.success('Leave request cancelled');
      fetchLeaveRequests();
      fetchBalances();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to cancel');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await useLeaveStore.getState().approveLeaveRequest(id);
      toast.success('Leave approved');
      fetchLeaveRequests();
      fetchBalances();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to approve');
    }
  };

  const openRejectDialog = (id: string) => {
    setRejectingId(id);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectingId) return;
    try {
      await useLeaveStore.getState().rejectLeaveRequest(rejectingId, rejectReason || undefined);
      toast.success('Leave rejected');
      setRejectDialogOpen(false);
      setRejectingId(null);
      fetchLeaveRequests();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to reject');
    }
  };

  // --------------------------------------------------------------- helpers

  const statusBadge = (status: string) => {
    const map: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
      approved: 'success', rejected: 'error', pending: 'warning', cancelled: 'default',
    };
    return <Badge variant={map[status] || 'default'}>{status}</Badge>;
  };

  const balanceColor = (code: string) => {
    const map: Record<string, { text: string; bg: string }> = {
      AL: { text: 'text-info', bg: 'bg-info/10 border-info/20' },
      SL: { text: 'text-warning', bg: 'bg-warning/10 border-warning/20' },
      CL: { text: 'text-success', bg: 'bg-success/10 border-success/20' },
      ML: { text: 'text-primary', bg: 'bg-primary/10 border-primary/20' },
      PL: { text: 'text-info', bg: 'bg-info/10 border-info/20' },
      BL: { text: 'text-destructive', bg: 'bg-destructive/10 border-destructive/20' },
    };
    return map[code] || { text: 'text-foreground', bg: 'bg-card border-border' };
  };

  const statusFilters: { value: StatusFilter; label: string; icon: React.ReactNode }[] = [
    { value: 'all', label: 'All', icon: <FileText className="size-3.5" /> },
    { value: 'pending', label: 'Pending', icon: <Clock className="size-3.5" /> },
    { value: 'approved', label: 'Approved', icon: <CheckCircle2 className="size-3.5" /> },
    { value: 'rejected', label: 'Rejected', icon: <XCircle className="size-3.5" /> },
  ];

  // --------------------------------------------------------------- render

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="tracking-tight text-foreground font-display text-page-title">Leave Management</h1>
        <p className="text-muted-foreground mt-1">Apply, track and manage time-off applications</p>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {balances.length === 0 && isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="p-4 rounded-lg border border-border bg-card">
              <Skeleton className="h-4 w-12 mb-3" />
              <Skeleton className="h-7 w-10 mb-2" />
              <Skeleton className="h-3 w-20" />
            </div>
          ))
        ) : balances.length === 0 ? (
          <div className="col-span-full text-center py-6 text-muted-foreground text-sm">
            No leave balances found for the current year.
          </div>
        ) : (
          balances.map((b, i) => {
            const colors = balanceColor(b.code);
            return (
              <div key={i} className={`p-4 rounded-lg border ${colors.bg} hover:-translate-y-0.5 transition-all duration-200 cursor-default`}>
                <div className="flex justify-between items-start mb-2">
                  <div className="px-2 py-0.5 bg-card rounded-md text-xs font-semibold text-muted-foreground">{b.code}</div>
                  <span className={`text-2xl font-semibold ${colors.text}`}>{b.remaining}</span>
                </div>
                <p className="text-xs font-semibold text-foreground leading-tight mb-1">{b.type}</p>
                <p className="text-xs text-muted-foreground">{b.used} used / {b.total} total</p>
                <div className="mt-2 h-1 bg-card rounded-full overflow-hidden">
                  <div
                    className={`h-full ${colors.text.replace('text-', 'bg-')} opacity-70`}
                    style={{ width: `${b.total > 0 ? (b.used / b.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="my-leaves">
        <TabsList variant="underline" className="w-full justify-start">
          <TabsTrigger variant="underline" value="my-leaves">My Leaves</TabsTrigger>
          <TabsTrigger variant="underline" value="apply">+ Apply</TabsTrigger>
          {canApprove && (
            <TabsTrigger variant="underline" value="approvals">
              Approvals
              {pendingApprovals.length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center size-5 rounded-full bg-warning text-warning-foreground text-xs font-semibold">
                  {pendingApprovals.length}
                </span>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger variant="underline" value="team-calendar">Team Calendar</TabsTrigger>
          <TabsTrigger variant="underline" value="policies">Policies</TabsTrigger>
        </TabsList>

        {/* ============================================ MY LEAVES ============================================ */}
        <TabsContent value="my-leaves">
          {/* Status filter */}
          <div className="flex gap-2 mb-4">
            {statusFilters.map(f => (
              <Button
                key={f.value}
                variant={statusFilter === f.value ? 'primary' : 'secondary'}
                size="xs"
                onClick={() => setStatusFilter(f.value)}
                leftIcon={f.icon}
              >
                {f.label}
              </Button>
            ))}
          </div>

          {isLoading && leaveRequests.length === 0 ? (
            <Card>
              <CardContent className="p-6 space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex gap-4 items-center">
                    <Skeleton className="size-14 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </div>
                ))}
              </CardContent>
            </Card>
          ) : filteredRequests.length === 0 ? (
            <EmptyState
              icon={<CalendarDays />}
              title={statusFilter === 'all' ? 'No leave requests yet' : `No ${statusFilter} requests`}
              description={statusFilter === 'all' ? 'Apply for leave using the "+ Apply" tab above.' : undefined}
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {filteredRequests.map(req => (
                    <LeaveRequestRow key={req.id} req={req} onCancel={handleCancel} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ============================================ APPLY ============================================ */}
        <TabsContent value="apply">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader className="px-6 py-4 border-b border-border">
                <h2 className="text-foreground font-display text-section-title">Apply for Leave</h2>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleApply} className="space-y-5">
                  {/* Leave type */}
                  <div>
                    <label className="block text-sm font-semibold text-muted-foreground mb-2">Leave Type *</label>
                    <select
                      value={formData.leaveTypeId}
                      onChange={e => setFormData({ ...formData, leaveTypeId: e.target.value })}
                      className="w-full px-4 py-3 bg-card border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {leaveTypes.map(lt => {
                        const bal = balances.find(b => b.code === lt.code);
                        return (
                          <option key={lt.id} value={lt.id}>
                            {lt.name} {bal ? `(${bal.remaining} remaining)` : lt.daysAllowed === 0 ? '(As needed)' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-muted-foreground mb-2">From Date *</label>
                      <Input
                        type="date"
                        value={formData.startDate}
                        onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-muted-foreground mb-2">To Date *</label>
                      <Input
                        type="date"
                        value={formData.endDate}
                        onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Half day toggle */}
                  <div className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.halfDay}
                        onChange={e => setFormData({ ...formData, halfDay: e.target.checked })}
                        className="w-4 h-4 rounded accent-primary"
                      />
                      <span className="text-sm text-muted-foreground font-medium">Half Day</span>
                    </label>
                    {formData.halfDay && (
                      <div className="flex gap-3">
                        {['first', 'second'].map(v => (
                          <label key={v} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="halfDayType"
                              value={v}
                              checked={formData.halfDayType === v}
                              onChange={() => setFormData({ ...formData, halfDayType: v as any })}
                              className="accent-primary"
                            />
                            <span className="text-sm text-muted-foreground capitalize">{v} Half</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Days summary */}
                  {calculatedDays > 0 && (
                    <div className="p-3 bg-info/5 border border-info/20 rounded-lg flex items-center gap-3">
                      <CalendarDays className="size-4 text-info" />
                      <span className="text-sm font-semibold text-foreground">
                        {calculatedDays} day{calculatedDays !== 1 ? 's' : ''}
                      </span>
                      {selectedTypeBalance && (
                        <span className="text-xs text-muted-foreground">
                          ({selectedTypeBalance.remaining} available)
                        </span>
                      )}
                    </div>
                  )}

                  {/* Approver — where this request routes */}
                  <div>
                    <label className="block text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                      <UserCheck className="size-4 text-primary" /> Applying to
                    </label>
                    {myApprover && myApprover.managers.length > 0 ? (
                      <>
                        <select
                          value={selectedManagerId}
                          onChange={e => setSelectedManagerId(e.target.value)}
                          className="w-full px-4 py-3 bg-card border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          {myApprover.managers.map((m, i) => (
                            <option key={m.id} value={m.id}>
                              {m.name}{i === 0 ? ' (primary)' : ''}
                            </option>
                          ))}
                        </select>
                        {myApprover.managers.length > 1 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            You report to {myApprover.managers.length} managers — choose who to send this request to.
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="p-3 bg-secondary/20 border border-border rounded-lg text-sm">
                        {myApprover?.fallbackName ? (
                          <span className="font-semibold text-foreground">
                            {myApprover.fallbackName}
                            <span className="text-xs text-muted-foreground font-normal"> · HR (no reporting manager assigned)</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground italic">HR team — ask your admin to assign a reporting manager</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-semibold text-muted-foreground mb-2">Reason *</label>
                    <Textarea
                      rows={3}
                      value={formData.reason}
                      onChange={e => setFormData({ ...formData, reason: e.target.value })}
                      placeholder="Brief reason for leave..."
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <Button type="submit" variant="primary" isLoading={isLoading}>
                      Submit Request
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => {
                      setFormData({ leaveTypeId: leaveTypes[0]?.id || '', startDate: '', endDate: '', halfDay: false, halfDayType: 'first', reason: '' });
                    }}>
                      Clear
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Sidebar: Who's on leave today */}
            <div className="space-y-4">
              <Card>
                <CardHeader className="px-4 py-3 border-b border-border">
                  <h3 className="text-foreground font-display text-card-title">Who's On Leave</h3>
                </CardHeader>
                <CardContent className="p-4">
                  {teamCalendar.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No one is on leave right now</p>
                  ) : (
                    <div className="space-y-3">
                      {teamCalendar.slice(0, 5).map((m, i) => (
                        <div key={i} className="text-sm">
                          <p className="font-semibold text-foreground">{m.firstName} {m.lastName}</p>
                          <p className="text-xs text-muted-foreground">
                            {m.type} • {new Date(m.startDate).toLocaleDateString()} – {new Date(m.endDate).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ============================================ APPROVALS ============================================ */}
        {canApprove && (
          <TabsContent value="approvals">
            <Card>
              <CardHeader className="px-6 py-4 border-b border-border">
                <h2 className="text-foreground font-display text-section-title">Approval Queue</h2>
              </CardHeader>
              <CardContent className="p-0">
                {pendingApprovals.length === 0 ? (
                  <EmptyState
                    icon={<CheckCircle2 />}
                    title="All caught up!"
                    description="No pending leave requests to approve."
                    className="border-none rounded-none"
                  />
                ) : (
                  <div className="divide-y divide-border">
                    {pendingApprovals.map(req => (
                      <div key={req.id} className="px-6 py-5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="size-10 rounded-lg bg-info/10 border border-info/20 flex items-center justify-center text-info font-semibold text-sm shrink-0">
                            {req.firstName?.[0]}{req.lastName?.[0]}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-foreground">{req.firstName} {req.lastName}</p>
                              {req.supportTicketNumber && (
                                <a
                                  href={`/support/tickets?search=${req.supportTicketNumber}`}
                                  className="inline-flex items-center gap-1 font-mono text-xs font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 hover:bg-blue-100 transition-colors"
                                  title="View Support Ticket in HR Queue"
                                >
                                  🎫 {req.supportTicketNumber}
                                </a>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {req.type} • {req.totalDays} day{req.totalDays > 1 ? 's' : ''} •{' '}
                              {new Date(req.startDate).toLocaleDateString()} – {new Date(req.endDate).toLocaleDateString()}
                            </p>
                            {req.reason && (
                              <p className="text-xs text-muted-foreground mt-0.5">"{req.reason}"</p>
                            )}
                            {req.department && (
                              <Badge variant="default" className="mt-1">{req.department}</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button variant="success" size="sm" onClick={() => handleApprove(req.id)}>
                            Approve
                          </Button>
                          <Button variant="danger" size="sm" onClick={() => openRejectDialog(req.id)}>
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reject reason dialog */}
            <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reject Leave Request</DialogTitle>
                  <DialogDescription>Provide a reason for rejecting this leave request.</DialogDescription>
                </DialogHeader>
                <Textarea
                  rows={3}
                  placeholder="Reason for rejection..."
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                />
                <DialogFooter>
                  <Button variant="secondary" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
                  <Button variant="danger" onClick={handleReject} isLoading={isLoading}>Reject</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* ============================================ TEAM CALENDAR ============================================ */}
        <TabsContent value="team-calendar">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Team on leave */}
            <Card>
              <CardHeader className="px-6 py-4 border-b border-border">
                <h2 className="text-foreground flex items-center gap-2 font-display text-section-title">
                  <Users className="size-4" /> Team Leave Calendar
                </h2>
              </CardHeader>
              <CardContent className="p-0">
                {teamCalendar.length === 0 ? (
                  <EmptyState
                    icon={<Users />}
                    title="No approved leaves this month"
                    description="Team members' approved leaves will appear here."
                    className="border-none rounded-none"
                  />
                ) : (
                  <div className="divide-y divide-border">
                    {teamCalendar.map((m, i) => (
                      <div key={i} className="px-6 py-4 flex items-center gap-4">
                        <div className="size-10 rounded-lg bg-warning/10 border border-warning/20 flex items-center justify-center font-semibold text-warning text-sm shrink-0">
                          {m.firstName?.[0]}{m.lastName?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground">{m.firstName} {m.lastName}</p>
                          <p className="text-sm text-muted-foreground">
                            {m.type} • {new Date(m.startDate).toLocaleDateString()} → {new Date(m.endDate).toLocaleDateString()}
                            {' '}• {m.totalDays} day{m.totalDays > 1 ? 's' : ''}
                          </p>
                        </div>
                        <Badge variant="warning">On Leave</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Holiday calendar */}
            <Card>
              <CardHeader className="px-6 py-4 border-b border-border">
                <h2 className="text-foreground flex items-center gap-2 font-display text-section-title">
                  <CalendarDays className="size-4" /> Holiday Calendar {new Date().getFullYear()}
                </h2>
              </CardHeader>
              <CardContent className="p-0">
                {holidays.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground text-sm">No holidays found</div>
                ) : (
                  <div className="divide-y divide-border">
                    {holidays.map((h, i) => {
                      const isPast = new Date(h.date) < new Date();
                      return (
                        <div key={i} className={`px-6 py-3 flex justify-between items-center ${isPast ? 'opacity-50' : ''}`}>
                          <div>
                            <p className="font-semibold text-foreground text-sm">{h.name}</p>
                            <Badge variant={h.type === 'Public' ? 'info' : 'warning'} className="mt-1">{h.type}</Badge>
                          </div>
                          <p className="font-semibold text-sm text-muted-foreground">
                            {new Date(h.date).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============================================ POLICIES ============================================ */}
        <TabsContent value="policies">
          <Card>
            <CardHeader className="px-6 py-4 border-b border-border">
              <h2 className="text-foreground font-display text-section-title">Leave Policies</h2>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-sm text-muted-foreground font-semibold border-b border-border">
                      <th className="text-left py-3 px-6">Leave Type</th>
                      <th className="text-center py-3 px-4">Code</th>
                      <th className="text-center py-3 px-4">Annual Days</th>
                      <th className="text-center py-3 px-4">Paid</th>
                      <th className="text-center py-3 px-4">Carry Forward</th>
                      <th className="text-center py-3 px-4">Max Carry</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {leaveTypes.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-muted-foreground">
                          No leave policies configured
                        </td>
                      </tr>
                    ) : (
                      leaveTypes.map((lt, i) => (
                        <tr key={i} className="hover:bg-card/50 transition-colors">
                          <td className="py-3 px-6 font-semibold text-foreground">{lt.name}</td>
                          <td className="py-3 px-4 text-center">
                            <Badge variant="default">{lt.code}</Badge>
                          </td>
                          <td className="py-3 px-4 text-center text-muted-foreground">
                            {lt.daysAllowed === 0 ? 'As needed' : lt.daysAllowed}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge variant={lt.paid ? 'success' : 'warning'}>{lt.paid ? 'Yes' : 'No'}</Badge>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge variant={lt.carryForward ? 'info' : 'default'}>{lt.carryForward ? 'Yes' : 'No'}</Badge>
                          </td>
                          <td className="py-3 px-4 text-center text-muted-foreground">
                            {lt.carryForward ? `Max ${lt.maxCarryForward}` : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// -----------------------------------------------------------------
// Sub-component: Leave request row
// -----------------------------------------------------------------

function LeaveRequestRow({ req, onCancel }: { req: LeaveRequest; onCancel: (id: string) => void }) {
  const statusVariant: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
    approved: 'success', rejected: 'error', pending: 'warning', cancelled: 'default',
  };

  return (
    <div className="px-6 py-4 flex items-center justify-between hover:bg-secondary/30 transition-colors">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-lg bg-card border border-border flex flex-col items-center justify-center shrink-0">
          <span className="text-lg font-semibold text-primary leading-tight">
            {new Date(req.startDate).getDate()}
          </span>
          <span className="text-xs text-muted-foreground font-semibold leading-tight">
            {new Date(req.startDate).toLocaleString('default', { month: 'short' })}
          </span>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-semibold text-foreground text-sm">{req.type}</p>
            {req.supportTicketNumber && (
              <a
                href={`/support/tickets?search=${req.supportTicketNumber}`}
                className="inline-flex items-center gap-1 font-mono text-xs font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 hover:bg-blue-100 transition-colors"
                title="View Support Ticket in HR Queue"
              >
                🎫 {req.supportTicketNumber}
              </a>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {new Date(req.startDate).toLocaleDateString()} – {new Date(req.endDate).toLocaleDateString()} • {req.totalDays} day{req.totalDays > 1 ? 's' : ''}
          </p>
          {req.reason && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-md">"{req.reason}"</p>
          )}
          {req.status === 'rejected' && req.rejectionReason && (
            <p className="text-xs text-destructive mt-0.5">Rejection: {req.rejectionReason}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <Badge variant={statusVariant[req.status] || 'default'}>{req.status}</Badge>
        {req.status === 'pending' && (
          <Button variant="ghost" size="xs" onClick={() => onCancel(req.id)} className="text-destructive hover:text-destructive">
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
